import React, { useState } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import Input from '@hobs/components/Input';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import PropTypes from 'prop-types';
import { H5 } from '@hobs/components/Headings';
import InputLabel from '@hobs/components/InputLabel';
import Switch from '@hobs/components/Switch';

import './UserPasswordManager.scss';
import { connect } from 'react-redux';
import Dialog from '@hobs/components/Dialog';
import { getUserResponsibilities } from '@hobs/actions/users';

const UserPasswordManager = ({
  resetPasswordSchema,
  user,
  onResetPasswordSubmit,
  onResendPasswordSubmit,
  onPasswordLockChange,
  onUserDeleted,
  resendDisabled,
  currentUserId,
  getUserResponsibilities
}) => {
  const [isChangeSubmitting, setIsChangeSubmitting] = useState(false);
  const [isUserDeleteDialogOpen, setIsUserDeleteDialogOpen] = useState(false);
  const [responsibilities, setResponsibilities] = useState({});
  const { t } = useTranslation();

  if (!user) {
    return <div></div>;
  }

  const renderResponsibilities = () => {
    if (Object.entries(responsibilities).length === 0) {
      return <div></div>;
    }

    return (
      <div>
        {responsibilities.sors.length !== 0 && <br />}
        {responsibilities.sors.map((sor, sorIndex) => (
          <ul key={sorIndex}>
            <li>
              User is configured as point of contact for
              <span>
                {sor.units.map((unit, unitIndex) => (
                  <span key={unitIndex}>
                    {' '}
                    {unit}
                    {sor.units.length != unitIndex + 1 ? ',' : ' '}
                  </span>
                ))}
              </span>
              units {sor.sorConfirmation && 'and sor confirmation'} in exercise{' '}
              {sor.exerciseName}.
            </li>
          </ul>
        ))}
        {!responsibilities.hasLoggedInWithinLast6Months && (
          <div>
            <br />
            Note that the user is not expired and has been logging into the
            system within the last 6 months.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-password-manager-view">
      <div className="user-password-manager-view--column">
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={resetPasswordSchema}
          enableReinitialize={true}
          initialValues={{
            password: '',
            confirmPassword: ''
          }}
          onSubmit={onResetPasswordSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <H5>{t('Change password')}</H5>
              <Field
                name="password"
                type="password"
                component={Input}
                disabled={user.isDeleted}
                autoComplete="off"
              >
                {t('Password')}
              </Field>
              <Field
                name="confirmPassword"
                type="password"
                component={Input}
                disabled={user.isDeleted}
                autoComplete="off"
              >
                {t('Confirm password')}
              </Field>
              <RaisedButton
                type="submit"
                disabled={isSubmitting || user.isDeleted}
              >
                {t('Change')}
              </RaisedButton>
            </Form>
          )}
        </Formik>
      </div>
      <div className="user-password-manager-view--column">
        <H5>{t('Resend password')}</H5>
        <InputLabel>{t('Email')}</InputLabel>
        <div className="user-password-manager-view--info">{user.email}</div>
        <RaisedButton
          onClick={onResendPasswordSubmit}
          disabled={resendDisabled || user.isDeleted}
        >
          {t('Resend')}
        </RaisedButton>
        <div className="user-password-manager-lock">
          <H5>{t('Account')}</H5>
          <div>
            <Switch
              checked={user.isPasswordLocked}
              onChange={isLocked => {
                onPasswordLockChange(isLocked);
              }}
              disabled={user.isDeleted}
              label={t(user.isPasswordLocked ? 'Locked' : 'Unlocked')}
            />
            {currentUserId !== user.userId && (
              <GhostButton
                danger
                size={'small'}
                disabled={isChangeSubmitting || user.isDeleted}
                onClick={async () => {
                  const responsibilitiesResponse = await getUserResponsibilities(
                    user.userId
                  );
                  setResponsibilities(responsibilitiesResponse.payload);
                  setIsUserDeleteDialogOpen(true);
                }}
              >
                {user.isDeleted ? 'Deleted' : 'Delete'}
              </GhostButton>
            )}
            <Dialog
              title={t('Delete user')}
              isOpen={isUserDeleteDialogOpen}
              onClose={() => setIsUserDeleteDialogOpen(false)}
              className="user-password-manager-view--dialog"
            >
              <div className="user-password-manager-view--dialog--message">
                Are you sure, you want to delete user {user.firstName}{' '}
                {user.lastName}? This action cannot be undone.
                {renderResponsibilities()}
              </div>
              <div className="inline-buttons">
                <GhostButton onClick={() => setIsUserDeleteDialogOpen(false)}>
                  {t('No')}
                </GhostButton>
                <RaisedButton
                  onClick={() => {
                    onUserDeleted(isSubmitting =>
                      setIsChangeSubmitting(isSubmitting)
                    );
                  }}
                  disabled={isChangeSubmitting}
                >
                  {t('Yes')}
                </RaisedButton>
              </div>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};

UserPasswordManager.propTypes = {
  t: PropTypes.func,
  user: PropTypes.object,
  resetPasswordSchema: PropTypes.object,
  onResetPasswordSubmit: PropTypes.func,
  onResendPasswordSubmit: PropTypes.func,
  onPasswordLockChange: PropTypes.func,
  onUserDeleted: PropTypes.func,
  resendDisabled: PropTypes.bool,
  currentUserId: PropTypes.string,
  getUserResponsibilities: PropTypes.func
};

const mapStateToProps = state => {
  return {
    currentUserId: state.auth.user.UserId
  };
};

const mapDispatchToProps = {
  getUserResponsibilities
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(UserPasswordManager));
